import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarDays, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportAppointmentReport,
  fetchAppointmentReport,
  formatReportDate,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function AppointmentReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchAppointmentReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load appointment report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportAppointmentReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity = (report?.summary?.appointment_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <CalendarDays className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Appointments</h2>
            <p className="text-sm text-muted-foreground">
              Booking source mix, status breakdown, and cancelled / no-show audit for scheduled dates.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Appointments" value={String(report.summary.appointment_count)} />
            <SummaryCard label="Completed" value={String(report.summary.completed_count)} />
            <SummaryCard label="Cancelled" value={String(report.summary.cancelled_count)} />
            <SummaryCard label="No-shows" value={String(report.summary.no_show_count)} />
            <SummaryCard
              label="Lost rate"
              value={`${report.summary.cancellation_rate}%`}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.appointment_count} appointments · lost rate{' '}
              {report.compare_summary.cancellation_rate}%
            </p>
          ) : null}

          <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
            <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              By booking source
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Completed</th>
                  <th className="px-4 py-3 font-medium">Cancelled</th>
                  <th className="px-4 py-3 font-medium">No-show</th>
                  <th className="px-4 py-3 font-medium">Share</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No appointments scheduled in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_source
                    .filter((row) => row.appointment_count > 0)
                    .map((row) => (
                      <tr key={row.source} className="border-t border-border/60">
                        <td className="px-4 py-3 font-medium">{row.source_label}</td>
                        <td className="px-4 py-3">{row.appointment_count}</td>
                        <td className="px-4 py-3">{row.completed_count}</td>
                        <td className="px-4 py-3">{row.cancelled_count}</td>
                        <td className="px-4 py-3">{row.no_show_count}</td>
                        <td className="px-4 py-3">{row.share_pct}%</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {report.by_cancellation_reason.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Cancellation & no-show reasons
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Reason</th>
                    <th className="px-4 py-3 font-medium">Total</th>
                    <th className="px-4 py-3 font-medium">Cancelled</th>
                    <th className="px-4 py-3 font-medium">No-show</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_cancellation_reason.map((row) => (
                    <tr
                      key={row.reason_code ?? row.reason_label}
                      className="border-t border-border/60"
                    >
                      <td className="px-4 py-3">{row.reason_label}</td>
                      <td className="px-4 py-3">{row.appointment_count}</td>
                      <td className="px-4 py-3">{row.cancelled_count}</td>
                      <td className="px-4 py-3">{row.no_show_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Cancelled & no-show log
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3 font-medium">Stylist</th>
                </tr>
              </thead>
              <tbody>
                {report.exceptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No cancellations or no-shows in this period.
                    </td>
                  </tr>
                ) : (
                  report.exceptions.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDate(row.starts_at)}</td>
                      <td className="px-4 py-3">{row.customer_name}</td>
                      <td className="px-4 py-3">{row.source_label}</td>
                      <td className="px-4 py-3 capitalize">{row.status.replace('_', '-')}</td>
                      <td className="px-4 py-3">{row.cancellation_reason_label ?? '—'}</td>
                      <td className="px-4 py-3">{row.stylist_name ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
